import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { getPage } from "../../../api/page";
import { Chart } from "../../../types/chart";
import { NotebookPage } from "../../../types/note";
import { ChartBlock } from "../../../types/notebook/note/block/chart";

// The chart's settings as they are now (query-omega-oql/pages/oql/chart-block.html): its Chart Source and each
// input's x-options are Method References (query-omega-oql/pages/oql/terms.html) -- a page by "path" or the older
// "page-id", a protocol and method (Query/run when omitted) and, unless inferred, an arity -- plus the stored input
// and the Chart Input Schema. Replaces the deprecated table "Inputs" tab (the record's older "inputs" file-ids).

type Ref = { path?: string; "page-id"?: string; protocol?: string; method?: string; arity?: number };

const pageHref = (appId: string, pageId: string) => `/app/${appId}/page/${pageId}`;

const methodLabel = (target: Ref) => `${target.protocol ?? "Query"}/${target.method ?? "run"}(input)`;

const refKind = (target: Ref) =>
  target.arity === undefined ? "Inferred Method Reference" : `Method Reference (arity ${target.arity})`;

// "string (month)", "string: a | b", or the bare type.
const typeOf = (prop: Record<string, any>): string =>
  prop.format
    ? `${prop.type} (${prop.format})`
    : Array.isArray(prop.enum)
      ? `${prop.type}: ${prop.enum.join(" | ")}`
      : String(prop.type ?? "");

const usePageName = (pageId: string): string | undefined => {
  const [name, setName] = useState<string | undefined>(undefined);
  useEffect(() => {
    getPage({ pageId })
      .then((p: NotebookPage) => setName(p["page-data"]?.name))
      .catch((e) => console.error("FAILED_TO_FETCH_CHART_SOURCE_PAGE", e));
  }, [pageId]);
  return name;
};

const PageLink: React.FC<{ appId: string; pageId: string; testId: string }> = ({ appId, pageId, testId }) => {
  const name = usePageName(pageId);
  return (
    <Link href={pageHref(appId, pageId)} data-testid={testId}>
      {name ?? pageId}
    </Link>
  );
};

// A reference's page: its Page Path as written, else its page-id as a linked page name.
const RefPage: React.FC<{ appId: string; target: Ref; testId: string }> = ({ appId, target, testId }) =>
  target.path ? (
    <code data-testid={testId}>{target.path}</code>
  ) : target["page-id"] ? (
    <PageLink appId={appId} pageId={target["page-id"]} testId={testId} />
  ) : (
    <span data-testid={testId}>—</span>
  );

export const SourceTab: React.FC<{ block: ChartBlock; chart: Chart }> = ({ block, chart }) => {
  const source = (chart.source ?? {}) as Ref;
  const hasPage = Boolean(source.path || source["page-id"]);
  const properties = (chart["input-schema"]?.schema.properties ?? {}) as Record<string, Record<string, any>>;
  const names = Object.keys(properties);
  return (
    <Box data-testid="chart-source-tab" sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
      <Typography variant="subtitle2">Chart Source</Typography>
      {hasPage ? (
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell>Query page</TableCell>
              <TableCell>
                <RefPage appId={block["app-id"]} target={source} testId="chart-source-page" />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Reference</TableCell>
              <TableCell data-testid="chart-source-kind">{refKind(source)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Method</TableCell>
              <TableCell data-testid="chart-source-method">
                <code>{methodLabel(source)}</code>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Stored input</TableCell>
              <TableCell data-testid="chart-source-input">
                <code>{JSON.stringify(chart.source?.input ?? {})}</code>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      ) : (
        <Typography data-testid="chart-source-none" color="text.secondary">
          No Chart Source: set source.path (or source.page-id) with qo write on the chart block path.
        </Typography>
      )}
      <Typography variant="subtitle2">Inputs</Typography>
      {names.length ? (
        <Table size="small" data-testid="chart-input-schema">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Choices from</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {names.map((name) => {
              const prop = properties[name];
              const options = prop["x-options"] as Ref | undefined;
              return (
                <TableRow key={name} data-testid={`chart-input-schema-${name}`}>
                  <TableCell>
                    <code>{name}</code>
                  </TableCell>
                  <TableCell>{String(prop.title ?? "")}</TableCell>
                  <TableCell>{typeOf(prop)}</TableCell>
                  <TableCell>
                    {options ? (
                      <RefPage appId={block["app-id"]} target={options} testId={`chart-input-options-${name}`} />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <Typography data-testid="chart-input-schema-none" color="text.secondary">
          No input-schema: viewers cannot change this chart's input.
        </Typography>
      )}
    </Box>
  );
};
